import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays } from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C } from './MobileShell';
import { isStaffArea } from './areas';
import { todayIsoLocal } from './homeMenu';
import { formatClocks, formatTime } from '@/lib/utils';

const inputCls = 'w-full rounded-lg px-3 py-2.5 text-sm outline-none';
const inputStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text };

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};

export default function MobileTerminplanung() {
  const { me, loading: meLoading, area } = useMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ lesson_date: todayIsoLocal(), start_time: '15:00' });
  const [error, setError] = useState('');
  const dateRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await mapi.get('/attendance-lists/prepaid-planning');
      const list = r.data?.items || [];
      setItems(list);
      setSelectedId((cur) => cur && list.some((i) => i.course_id === cur) ? cur : (list[0]?.course_id || null));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Kartenkurse konnten nicht geladen werden');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (me && isStaffArea(area)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, area]);

  if (meLoading) {
    return <MobileShell><div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div></MobileShell>;
  }
  if (!me) return <Navigate to="/m/login" replace />;
  if (!isStaffArea(area)) return <Navigate to="/m" replace />;

  const selected = items.find((i) => i.course_id === selectedId) || null;

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!form.lesson_date || !form.start_time) {
      toast.error('Datum und Uhrzeit angeben');
      return;
    }
    setSaving(true);
    try {
      await mapi.post('/attendance-lists/prepaid-planning', {
        course_id: selected.course_id,
        lesson_date: form.lesson_date,
        start_time: form.start_time,
      });
      toast.success('Termin gesetzt — erscheint in der Anwesenheitsliste');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Termin konnte nicht gesetzt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileShell onAction={selected ? () => dateRef.current?.focus() : undefined}>
      <MobileHeader title="Terminplanung" backTo="/m" />
      <div className="flex-1 px-4 pb-8 space-y-3" data-testid="mobile-terminplanung-page">
        <p className="text-xs mt-1" style={{ color: C.dim }}>
          10er-, 8er- und 4er-Karten: Termine hier einzeln setzen. In der Anwesenheitsliste erscheinen nur gesetzte Termine.
        </p>
        {error && (
          <div className="rounded-xl px-3 py-2 text-xs" style={{ background: '#7f1d1d', color: '#fca5a5' }}>{error}</div>
        )}
        {loading && <p className="text-sm" style={{ color: C.dim }}>Laden…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm py-6 text-center" style={{ color: C.dim }} data-testid="mobile-terminplanung-empty">
            Keine 4er-, 8er- oder 10er-Kartenkurse gefunden.
          </p>
        )}
        {items.map((item) => (
          <button
            key={item.course_id}
            type="button"
            onClick={() => setSelectedId(item.course_id)}
            data-testid={`mobile-terminplanung-course-${item.course_id}`}
            className="w-full text-left rounded-2xl px-4 py-3"
            style={{
              background: C.card,
              border: `1px solid ${selectedId === item.course_id ? C.red : C.border}`,
            }}
          >
            <div className="text-sm font-semibold">{formatClocks(item.course_name) || item.course_name}</div>
            {(item.students || []).map((s) => (
              <div key={s.student_id} className="text-[11px] mt-0.5" style={{ color: C.dim }}>
                {s.name}
                {s.prepaid
                  ? ` · ${s.prepaid.used}/${s.prepaid.total} · ${s.prepaid.remaining} übrig`
                  : ''}
              </div>
            ))}
          </button>
        ))}

        {selected && (
          <form onSubmit={submit} className="rounded-2xl p-4 space-y-3" style={{ background: C.card, border: `1px solid ${C.border}` }}
                data-testid="mobile-terminplanung-form">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CalendarDays className="w-4 h-4" style={{ color: C.red }} />
              Termin setzen
            </div>
            <label className="block text-[11px] font-semibold" style={{ color: C.dim }}>
              Datum
              <input
                ref={dateRef}
                type="date"
                className={`${inputCls} mt-1`}
                style={inputStyle}
                value={form.lesson_date}
                onChange={(e) => setForm((f) => ({ ...f, lesson_date: e.target.value }))}
                data-testid="mobile-terminplanung-date"
              />
            </label>
            <label className="block text-[11px] font-semibold" style={{ color: C.dim }}>
              Beginn
              <input
                type="time"
                className={`${inputCls} mt-1`}
                style={inputStyle}
                value={formatTime(form.start_time) || ''}
                onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                data-testid="mobile-terminplanung-time"
              />
            </label>
            <button
              type="submit"
              disabled={saving}
              data-testid="mobile-terminplanung-save"
              className="w-full rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-50"
              style={{ background: C.red }}
            >
              {saving ? 'Speichern…' : 'Termin in die Anwesenheitsliste setzen'}
            </button>
            {(selected.upcoming || []).length > 0 && (
              <div className="pt-1">
                <p className="text-[11px] font-semibold mb-1" style={{ color: C.dim }}>Gesetzte Termine</p>
                {selected.upcoming.map((u) => (
                  <p key={u.lesson_id} className="text-xs" style={{ color: C.text }}
                     data-testid={`mobile-terminplanung-upcoming-${u.lesson_id}`}>
                    {fmtDate(u.date)} · {u.start}–{u.end}
                  </p>
                ))}
              </div>
            )}
          </form>
        )}
      </div>
    </MobileShell>
  );
}
